/**
 * Options accepted by {@link fetch}. A `RequestInit` whose `headers` are a
 * plain string record — the runtime mutates that record directly
 * (`headers['X-Requested-With'] = ...`), so we narrow `headers` away from the
 * DOM union (`Headers | string[][] | Record<string, string>`) to the mutable
 * record form the call sites actually pass.
 */
export interface FetchOptions extends Omit<RequestInit, 'headers'> {
  headers?: Record<string, string>;
}

import { getAccessToken } from './auth/accessToken';
import { refreshAccessToken } from './auth/refresh';

/**
 * Build the request init for a single attempt.
 *
 * Starts from the caller's options, always sets the `X-Requested-With` CSRF
 * header, defaults `credentials` to `'same-origin'` (so BOTH the session cookie
 * — still honored by the backend's dual-accept — and any cookie-scoped routes
 * keep working, and direct-navigation downloads that rely on the session cookie
 * are unaffected) and, when an access token is held, attaches the
 * `Authorization: Bearer <token>` header so the request authenticates over the
 * Bearer path. The headers are rebuilt per attempt so the retry picks up the
 * token minted by the in-between refresh.
 */
function buildInit(opts: FetchOptions): RequestInit {
  const headers: Record<string, string> = Object.assign({}, opts.headers);
  headers['X-Requested-With'] = 'Merlin';
  const token = getAccessToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return Object.assign(
    {
      credentials: 'same-origin',
    },
    opts,
    {
      headers,
    }
  );
}

/**
 * Wrap window.fetch
 * (https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API) to:
 *  - add the `X-Requested-With` CSRF header,
 *  - default `credentials` to `'same-origin'` so the session cookie still flows
 *    (dual-accept; direct-navigation downloads keep relying on it),
 *  - attach `Authorization: Bearer <accessToken>` when one is held, and
 *  - on a 401, refresh the access token ONCE (a single shared in-flight refresh,
 *    so concurrent 401s do not stampede the refresh endpoint) and retry the
 *    original request once with the new token. If the refresh itself fails
 *    (cookie missing/invalid/revoked → logged out), the original 401 response is
 *    returned unchanged so the existing unauthenticated/login UX takes over.
 */
export function fetch(url: string, opts: FetchOptions): Promise<Response> {
  return window.fetch(url, buildInit(opts)).then((response) => {
    if (response.status !== 401) {
      return response;
    }
    // A 401: try to mint a fresh access token and replay the request once. The
    // refresh shares one in-flight promise across concurrent 401s. On refresh
    // failure, fall back to the original 401 (logged-out path) — never loop.
    return refreshAccessToken().then(
      () => window.fetch(url, buildInit(opts)),
      () => response
    );
  });
}
